import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import {
  setTokens,
  clearTokens,
  registerUnauthHandler,
  getMe,
  signOut as apiSignOut,
  type AuthUser,
} from '../lib/api';

interface AuthState {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextValue extends AuthState {
  /** Called by the sign-in page after receiving tokens from the API. */
  onTokensReceived: (access: string, refresh: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
  });

  const loadUser = useCallback(async () => {
    const access = localStorage.getItem('anchor_access');
    const refresh = localStorage.getItem('anchor_refresh');
    if (!access || !refresh) {
      setState({ user: null, isLoading: false, isAuthenticated: false });
      return;
    }
    setTokens(access, refresh);
    try {
      const user = await getMe();
      setState({ user, isLoading: false, isAuthenticated: true });
    } catch {
      clearTokens();
      localStorage.removeItem('anchor_access');
      localStorage.removeItem('anchor_refresh');
      setState({ user: null, isLoading: false, isAuthenticated: false });
    }
  }, []);

  useEffect(() => {
    registerUnauthHandler(() => {
      clearTokens();
      localStorage.removeItem('anchor_access');
      localStorage.removeItem('anchor_refresh');
      setState({ user: null, isLoading: false, isAuthenticated: false });
    });
    loadUser();
  }, [loadUser]);

  const onTokensReceived = useCallback(
    async (access: string, refresh: string) => {
      setTokens(access, refresh);
      localStorage.setItem('anchor_access', access);
      localStorage.setItem('anchor_refresh', refresh);
      const user = await getMe();
      setState({ user, isLoading: false, isAuthenticated: true });
    },
    [],
  );

  const signOut = useCallback(async () => {
    await apiSignOut();
    setState({ user: null, isLoading: false, isAuthenticated: false });
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, onTokensReceived, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
